import { useMemo, useState } from "react"
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from "@ui/shad"
import { ChevronDown, Loader2, Tag } from "lucide-react"

import { useCreateTagMutation, useFetchTags } from "@/hooks/api/tags"

const MAX_TAG_LENGTH = 16

interface BulkTagSelectionProps {
  onSelect: (tagId: string) => void
  isDisabled?: boolean
  isUpdating?: boolean
}

export default function BulkTagSelection({ onSelect, isDisabled, isUpdating }: BulkTagSelectionProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const { data: tagsResponse } = useFetchTags("household")
  const { mutate: createTag, isPending: isCreating } = useCreateTagMutation()

  const options = useMemo(() => {
    const tagOptions =
      tagsResponse?.tags?.map((tag) => ({
        value: tag.id,
        label: tag.name,
      })) || []

    if (!search) return tagOptions

    return tagOptions.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()))
  }, [tagsResponse?.tags, search])

  const trimmedSearch = search.trim()
  const tagAlreadyExists = tagsResponse?.tags?.some((tag) => tag.name.toLowerCase() === trimmedSearch.toLowerCase())
  const canCreateTag = trimmedSearch.length > 0 && trimmedSearch.length <= MAX_TAG_LENGTH && !tagAlreadyExists

  function handleSelect(tagId: string) {
    onSelect(tagId)
    setTimeout(() => setOpen(false), 0)
  }

  function handleCreateTag() {
    if (!canCreateTag || isCreating) return

    createTag(
      { name: trimmedSearch, entity_type: "household" },
      {
        onSuccess: (newTag) => {
          setSearch("")
          handleSelect(newTag.id)
        },
      }
    )
  }

  function handleSearchChange(value: string) {
    setSearch(value)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="inverse" size="sm" disabled={isDisabled}>
          {isUpdating ? <Loader2 className="animate-spin" /> : <Tag />}
          Add tag
          <ChevronDown className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search tags..." value={search} onValueChange={handleSearchChange} />
          <CommandList className="p-0">
            <CommandEmpty className="text-muted-foreground p-2 text-center text-xs">No tags found.</CommandEmpty>
            <ScrollArea className="h-full">
              <CommandGroup className="max-h-56 p-1.5">
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => handleSelect(option.value)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2">{option.label}</div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
          {canCreateTag && (
            <div className="flex flex-col items-stretch gap-1 border-t p-2">
              <div className="text-muted-foreground text-xs">Create</div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCreateTag}
                className="flex w-full flex-col justify-center"
                disabled={isCreating}
              >
                {isCreating ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <span className="ml-1 font-semibold">&ldquo;{trimmedSearch}&rdquo;</span>
                )}
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
